import {announce} from '@react-aria/live-announcer'
import {
  Button,
  Dialog,
  DialogTrigger,
  Heading,
  Modal,
  ModalOverlay,
  Text,
} from 'react-aria-components'
import {toast} from 'sonner'

import {useLocale} from '@/lib/locale/locale'
import {getOrdersQueryKey} from '@/lib/order/hooks/useOrders'
import {cancelOrder} from '@/services/orderService'
import toastErrorMessage from '@/utils/errors/toastErrorMessage'

import style from './CancelDialog.module.scss'

const CancelDialog = deepMemo<
  PropsWithChildren<{orderId: string; onOpenChange?: (isOpen: boolean) => void}>
>(function CancelDialog({children, orderId, onOpenChange}) {
  const queryClient = useQueryClient()
  const [locale] = useLocale()

  const onCancel = useCallback(
    (action: () => void) => {
      action()
      // TODO: convert to mutation
      void cancelOrder({orderId, locale})
        .then(result => {
          if (result === true) {
            toast.success('Cancellation requested!')
            announce('Cancellation requested!')
          } else {
            toastErrorMessage(result)
          }
        })
        .catch(() => {
          toastErrorMessage('Unexpected error, please try again later!')
        })
        .finally(() => {
          void queryClient.invalidateQueries({
            queryKey: getOrdersQueryKey({locale}),
            exact: true,
          })
        })
    },
    [orderId, queryClient, locale],
  )

  return (
    <DialogTrigger onOpenChange={onOpenChange}>
      {children}
      <ModalOverlay
        // TODO: add animation
        className={({isEntering, isExiting}) =>
          clsx(style.Overlay, isEntering && style.IsEntering, isExiting && style.IsExiting)
        }
        isDismissable
      >
        <Modal className={style.Modal}>
          <Dialog className={style.Dialog} aria-describedby='description'>
            {({close}) => (
              <>
                <Heading className={style.Heading} slot='title'>
                  Cancelation request
                </Heading>
                <div className={style.Description} id='description'>
                  <Text>Are you sure you want to cancel this order?</Text>
                </div>
                <div className={style.Action}>
                  <Button className={style.Cancel} onPress={close}>
                    No
                  </Button>
                  <Button
                    className={style.Ok}
                    onPress={() => {
                      onCancel(close)
                    }}
                  >
                    Yes
                  </Button>
                </div>
              </>
            )}
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  )
})()

export default CancelDialog
